import { PrimaryButton } from "@fluentui/react";
import * as Adaptive from "adaptivecards";
import { TypedCardElementPeer, PropertySheet, PropertySheetCategory, StringPropertyEditor } from "adaptivecards-designer";
import React from "react";

import { createRoot } from 'react-dom/client';
import { FileProps } from "../../shared/components/EditTicketAttachments/EditTicketAttachments";
import { Comment } from '../../shared/interfaces/ticket.interface';
import { removeOtherPropertyConfigurations } from "./Utils/CustomElementUtils";

export interface UploadFileProps {
    Files: FileProps[],
    Comment: Comment
}

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);

// Attachment urls come from uploaded/back-end data, so only allow schemes that
// navigate - anything else (javascript:, data:, ...) is rendered as plain text.
const getSafeDownloadUrl = (url: string | undefined): string | undefined => {
    if(!url)
        return undefined;

    try {
        const scheme = new URL(url, document.baseURI).protocol.toLowerCase();
        return scheme === "http:" || scheme === "https:" || scheme === "mailto:" ? url : undefined;
    } catch {
        return undefined;
    }
};

export class UploadFile extends Adaptive.TextInput {
    static readonly JsonTypeName = "Add Attachment";
    static readonly ElementId = "Ticket.UploadFile";
    static readonly defaultLabel = "Add Attachment";
    static readonly displayLabelProperty = displayLabelProperty;
    
    static onOpenUploadFromCardAction: () => void;
    static filesToUpload: FileProps[];
    static message: string;
    static messageIsPrivate: boolean;
    static key: string;
    static disableField: boolean;
    static clear: boolean;

    //#region Schema

    @Adaptive.property(displayLabelProperty)
    get displayLabel(): string | undefined {
        return this.getValue(UploadFile.displayLabelProperty);
    }

    set displayLabel(value: string | undefined) {
        this.setValue(UploadFile.displayLabelProperty, value);
        this.updateLayout();
    }

    //#endregion

    protected internalRender(): HTMLElement | undefined {
        this.id = UploadFile.ElementId;
        if((this?.displayLabel ?? "").length == 0)
            this.displayLabel = UploadFile.defaultLabel;

        let element = document.createElement("div");
        element.setAttribute("style", "width: 100%");

        if (UploadFile.clear) UploadFile.filesToUpload = [];
        
        if(UploadFile.filesToUpload?.length > 0){
            const heading = document.createElement("p");
            const headingLabel = document.createElement("strong");
            headingLabel.textContent = "Attachments: ";
            heading.appendChild(headingLabel);
            element.appendChild(heading);

            const attachmentsContainer = document.createElement("div");
            attachmentsContainer.setAttribute("style", "background-color: var(--mgt-theme-background2);padding: 1em;margin: 1em 0;");

            if(UploadFile.messageIsPrivate){
                const privateLabel = document.createElement("p");
                privateLabel.setAttribute("style", "text-align: right;color: red;");
                privateLabel.textContent = "(Private)";
                attachmentsContainer.appendChild(privateLabel);
            }
            if(UploadFile.message){
                const messageParagraph = document.createElement("p");
                messageParagraph.className = UploadFile.messageIsPrivate ? 'mt-2 mb-2' : 'mb-2';
                messageParagraph.textContent = UploadFile.message;
                attachmentsContainer.appendChild(messageParagraph);
            }
            UploadFile.filesToUpload?.forEach(file => {
                const fileParagraph = document.createElement("p");
                const safeUrl = getSafeDownloadUrl(file.DownladUrl);
                if(safeUrl){
                    const fileLink = document.createElement("a");
                    fileLink.className = "mb-1 tikit-card-files";
                    fileLink.setAttribute("style", "color: var(--teams-font-color)");
                    fileLink.href = safeUrl;
                    fileLink.target = "_blank";
                    fileLink.rel = "noopener noreferrer";
                    fileLink.textContent = file.FileName;
                    fileParagraph.appendChild(fileLink);
                }else{
                    fileParagraph.className = "mb-1 tikit-card-files";
                    fileParagraph.setAttribute("style", "color: var(--mgt-theme-default-foreground2)");
                    fileParagraph.textContent = file.FileName;
                }
                attachmentsContainer.appendChild(fileParagraph);
            });
            attachmentsContainer.appendChild(document.createElement("p"));
            element.appendChild(attachmentsContainer);

            const filesToUpload = UploadFile.filesToUpload.map(x => { return { FileName: x.FileName}; });

            this.setValue(UploadFile.valueProperty, JSON.stringify({Files:filesToUpload, Comment: {Body: UploadFile.message, IsPublic: !UploadFile.messageIsPrivate} }));
        }else
            this.setValue(UploadFile.valueProperty, "");
        
        

        let renderedElement = document.createElement("div");
        renderedElement.setAttribute("style", "width: 100%");

        const domNode = document.createElement('div');
        const root = createRoot(domNode); 
        root.render(<PrimaryButton key={UploadFile.key} text={this.displayLabel} className="tikit-button" 
            onClick={() => {
                UploadFile.onOpenUploadFromCardAction && UploadFile.onOpenUploadFromCardAction();
            }}
            {...UploadFile.disableField && {disabled: true}}
            />);

            if(UploadFile.filesToUpload?.length > 0)
                renderedElement.append(element, domNode);
            else
                renderedElement.append(domNode);

        return renderedElement;
    }

    getJsonTypeName(): string {
        return UploadFile.JsonTypeName;
    }

    get value(): string {
        return this.getValue(UploadFile.valueProperty);
    }

    updateLayout(processChildren: boolean = true) {
        super.updateLayout(processChildren);
    }
}

export class UploadFilePeer extends TypedCardElementPeer<UploadFile> {
    static readonly displayLabelProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "displayLabel", "Label", false);

    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);

        propertySheet.add(
            defaultCategory,
            UploadFilePeer.idProperty,
            UploadFilePeer.displayLabelProperty);
    }
}